import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useChatChatroomSocketSendEvents, useFlightRooms } from '@connect-x-app/hybrid-state';
import { FlightRoomSummary, FlightStatus } from '@connect-x-app/shared-domain-models';
import { isRoomLanded } from '@connect-x-app/shared-utils';
import { FLIGHTROOM_TEST_IDS } from '@connect-x-app/web-test-ids';
import { Button, Modal, ModalOpener } from '@connect-x-app/web-ui';

type Props = {
  chatroomId: string;
  content: string;
  onError?: () => void;
  onSuccess?: () => void;
  room?: FlightRoomSummary;
  title: string;
};

export const RequestFlightroomAccessModal = ({ chatroomId, content, onError, onSuccess, room, title }: Props) => {
  const navigate = useNavigate();
  const { setSelectedFlightRoomTab } = useFlightRooms();

  const { sendRequestAccess } = useChatChatroomSocketSendEvents();
  const [isPending, setIsPending] = useState(false);
  const { t } = useTranslation();

  const onRequestAccess = async () => {
    setIsPending(true);
    const result = await sendRequestAccess({ chatroomId });
    setIsPending(false);

    if (result?.ok) {
      navigate('/dashboard/flightrooms');
      setSelectedFlightRoomTab(room && isRoomLanded(room) ? FlightStatus.LANDED : FlightStatus.ACTIVE);
      onSuccess?.();
      ModalOpener.instance.close();
    } else {
      ModalOpener.instance.close();
      onError?.();
    }
  };

  const onCancel = () => {
    if (!isPending) {
      ModalOpener.instance.close();
    }
  };

  return (
    <Modal onCancel={onCancel}>
      <Modal.Header onCancel={onCancel}>{title}</Modal.Header>
      <Modal.Content>{content}</Modal.Content>
      <Modal.Buttons>
        <Button isOutline onClick={onCancel} testId={FLIGHTROOM_TEST_IDS.requestAccess.cancel} theme="dark">
          {t('SHARED.BUTTONS.CANCEL')}
        </Button>
        <Button loading={isPending} onClick={onRequestAccess} testId={FLIGHTROOM_TEST_IDS.requestAccess.request} theme="primary">
          {t('FLIGHTROOMS.REQUEST_ACCESS.REQUEST')}
        </Button>
      </Modal.Buttons>
    </Modal>
  );
};
